import { Link } from "@tanstack/react-router";
import { ShieldCheck, Wrench, MessageSquareText, Car, MapPin, Star, ArrowRight } from "lucide-react";
import { trackEvent } from "@/lib/tracking";

interface Props {
  /** Page slug for tracking attribution. */
  page: string;
}

const REASONS = [
  {
    icon: ShieldCheck,
    title: "DVSA-authorised MOT station",
    body: "MOT classes 1, 2, 3, 4, 5 and 7 at station V106376.",
  },
  {
    icon: Wrench,
    title: "Everything under one roof",
    body: "MOTs, servicing, diagnostics, mechanical repairs and bodywork from one workshop.",
  },
  {
    icon: MessageSquareText,
    title: "Clear advice before work",
    body: "We explain what needs doing before repair work starts.",
  },
  {
    icon: Car,
    title: "Useful for high-mileage drivers",
    body: "Support for commuters, vans, delivery drivers and private-hire vehicles.",
  },
  {
    icon: MapPin,
    title: "Real local workshop",
    body: "Find us via the yard entrance at 14 Copenhagen Place, E14 7DF.",
  },
  {
    icon: Star,
    title: "Local review proof",
    body: "Rated by local drivers on Google.",
  },
] as const;

const LINKS: { label: string; to: string }[] = [
  { label: "MOT", to: "/mot" },
  { label: "Servicing", to: "/servicing" },
  { label: "Diagnostics", to: "/diagnostics" },
  { label: "Mechanical repairs", to: "/mechanical-repairs" },
  { label: "Bodywork", to: "/bodywork" },
  { label: "Reviews", to: "/reviews" },
  { label: "Verify business", to: "/official-business-information" },
  { label: "Request booking", to: "/book" },
  { label: "Contact", to: "/contact" },
];

/**
 * Honest local comparison block — "Why local drivers choose Woodseer Autos".
 * Used on the homepage and on every money service/landing page. Tracks
 * outbound clicks via `service_money_page_cta_click` with cta_type="why_us_link".
 */
export function WhyChooseWoodseer({ page }: Props) {
  return (
    <section
      aria-labelledby={`why-woodseer-${page}`}
      className="section-y bg-secondary/40 border-y border-border"
    >
      <div className="mx-auto max-w-6xl px-4 md:px-6">
        <div className="max-w-2xl">
          <p className="eyebrow">Honest local comparison</p>
          <h2 id={`why-woodseer-${page}`} className="mt-3 display-lg text-foreground">
            Why local drivers choose Woodseer Autos.
          </h2>
          <p className="mt-4 text-muted-foreground leading-relaxed max-w-xl">
            We're not the cheapest in a national price war and we're not a chain. We're an independent
            East London garage focused on doing the job properly and being straight with you.
          </p>
        </div>

        <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {REASONS.map(({ icon: Icon, title, body }) => (
            <div
              key={title}
              className="rounded-2xl border border-border bg-card p-5 md:p-6 hover:border-primary/40 transition-colors"
            >
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <Icon className="h-5 w-5" />
              </div>
              <h3 className="mt-4 text-base font-bold text-foreground tracking-tight">{title}</h3>
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{body}</p>
            </div>
          ))}
        </div>

        <div className="mt-8 flex flex-wrap gap-2">
          {LINKS.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              onClick={() =>
                trackEvent("service_money_page_cta_click", {
                  page,
                  cta_type: "why_us_link",
                  position: "why_us",
                  target: l.to,
                })
              }
              className="inline-flex items-center gap-1 rounded-full border border-border bg-background px-3.5 py-1.5 text-xs font-bold text-foreground hover:border-primary/40 hover:text-primary transition-colors"
            >
              {l.label} <ArrowRight className="h-3 w-3" />
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
